// Typed wrappers around the /api/auto_research/* + Test-scoped Auto
// Research endpoints (Phase 5A). Wire shapes are validated through
// the Zod schemas in ./schemas which mirror the Pydantic models in
// damperlib/api_models.py — kept in lockstep by the contract tests
// (tests/test_api_contract.py + frontend/tests/api-contract.test.ts).
import { z } from "zod";
import {
  AutoResearchSessionSchema,
  AutoResearchSessionListItemSchema,
  type AutoResearchSession,
  type AutoResearchSessionListItem,
} from "./schemas";

export type { AutoResearchSession, AutoResearchSessionListItem };

export type StartAutoResearchBody = {
  max_runs?: number;
  seed?: number;
  // Target fields are a group: backend rejects partial sets with 422.
  // Pass all three (kpi + direction + value) or none.
  target_kpi?: string | null;
  target_direction?: "max" | "min" | null;
  target_value?: number | null;
  // Phase 5B driver contract: send objective_id to opt into the
  // kpi_evaluators.evaluate(...) flow. Mutually exclusive with the
  // legacy target_* fields (backend returns 422 if both are set).
  objective_id?: string | null;
  objective_target_value?: number | null;
  constraint_overrides?: Record<string, number> | null;
  // Deprecated no-op (2026-06-14): the experimental-trust START gate was
  // removed (every rig objective is experimental by design). The frontend no
  // longer sends this; the backend still accepts but ignores it. Retained for
  // back-compat only.
  allow_experimental_objective?: boolean;
  // Phase 5C: which proposal source drives the loop.
  //   'click_sweep' (default, backwards-compatible): Phase 5A's
  //     deterministic generator. With objective_id set, the legacy
  //     click sweep is scored through kpi_evaluators.evaluate
  //     (Phase 5B).
  //   'claude': Phase 5C — Claude proposes one overlay per tick.
  //     REQUIRES objective_id. The backend refuses generator='claude'
  //     without an objective.
  generator?: "click_sweep" | "claude";
  // T1c exploration knobs (2026-06-17): per-session overrides for the plateau
  // early-stop. Omit / null = backend defaults; raise plateau_window or set
  // plateau_disabled to make Auto Research explore more before converging.
  plateau_window?: number | null;
  plateau_rel_eps?: number | null;
  plateau_disabled?: boolean;
  // T3 Pareto multi-objective (2026-06-17): optimise ≥2 objectives at once (a
  // non-dominated front). objective_ids[0] is the primary; the backend derives
  // objective_id from it. Mutually exclusive with constraint_overrides.
  objective_ids?: string[] | null;
  // Protocol mode (2026-07-12): restructure the Claude loop into DESIGN ->
  // SCREEN -> EXPLOIT -> VERIFY. Claude-generator only; the backend 422s it for
  // any other generator. Existing modes stay byte-identical when omitted/false.
  protocol?: boolean;
};


// ---------- Phase 5C event log ----------

export type AutoResearchEvent = {
  id: number;
  session_id: number;
  kind: string;
  ts: string;
  payload: Record<string, unknown>;
};

export type AutoResearchEventsResponse = {
  session_id: number;
  since_id: number;
  next_since_id: number;
  events: AutoResearchEvent[];
};

export async function fetchAutoResearchEvents(
  sessionId: number,
  sinceId: number = 0,
  limit: number = 200,
): Promise<AutoResearchEventsResponse> {
  const url = `/api/auto_research/sessions/${sessionId}/events`
    + `?since_id=${sinceId}&limit=${limit}`;
  const r = await fetch(url);
  if (!r.ok) {
    throw new Error(`GET ${url} failed: ${r.status}`);
  }
  return (await r.json()) as AutoResearchEventsResponse;
}


// ---------- Phase 5C event-log presentation helpers ----------
//
// Each event row carries an untyped JSON `payload`. These pure helpers
// turn the interesting kinds into one-line summaries and reconcile
// Claude's logged predictions against the realised rig KPIs. Everything
// here is defensive: payloads are wire JSON, so a helper that meets a
// missing or wrong-typed field returns null (or a neutral row) rather
// than throwing, because a thrown render would blank the whole panel.

/** Coerce untyped JSON to a finite number, else null. Numeric strings
 *  are accepted so a KPI serialised as text still reconciles. */
function asFiniteNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Compact fixed format for the summary strings (trailing zeros
 *  stripped). The reconciliation table uses its own signed formatter. */
function _round4(v: number): string {
  return v.toFixed(4).replace(/\.?0+$/, "");
}

/** A normalized fv_scale is a list of {corners:[...], factor} entries.
 *  Render it as "lf,rf×1.15 lr,rr×0.9", or null when nothing parses. */
function summarizeFvScale(fv: unknown): string | null {
  if (!Array.isArray(fv)) return null;
  const entries = fv
    .map((e) => {
      if (!e || typeof e !== "object") return null;
      const rec = e as Record<string, unknown>;
      const factor = asFiniteNumber(rec.factor);
      if (factor === null) return null;
      const corners = Array.isArray(rec.corners)
        ? (rec.corners as unknown[]).join(",")
        : "?";
      return `${corners}×${factor}`;
    })
    .filter((x): x is string => x !== null);
  return entries.length > 0 ? entries.join(" ") : null;
}

/** run_enqueued payload → "run 12: clicks lf [10,11,12,13] · fv_scale ...". */
function summarizeOverlay(payload: Record<string, unknown>): string {
  const runId = asFiniteNumber(payload.run_id);
  const head = runId !== null ? `run ${runId}` : "proposal";
  const overlay = payload.overlay;
  if (!overlay || typeof overlay !== "object") return head;
  const o = overlay as Record<string, unknown>;
  const parts: string[] = [];
  const corners = o.corners;
  if (corners && typeof corners === "object") {
    const cornerMap = corners as Record<string, unknown>;
    const names = Object.keys(cornerMap);
    if (names.length > 0) {
      const cs = names.map((c) => {
        const cv = cornerMap[c];
        const clicks =
          cv && typeof cv === "object"
            && Array.isArray((cv as Record<string, unknown>).clicks)
            ? ((cv as Record<string, unknown>).clicks as unknown[])
            : null;
        return clicks ? `${c} [${clicks.join(",")}]` : c;
      });
      parts.push(`clicks ${cs.join(" ")}`);
    }
  }
  const fv = summarizeFvScale(o.fv_scale);
  if (fv) parts.push(`fv_scale ${fv}`);
  return parts.length > 0 ? `${head}: ${parts.join(" · ")}` : head;
}

/**
 * One-line, per-kind summary of an event payload for the collapsible
 * event log. Returns null for kinds we do not specifically recognise
 * (the panel then offers a raw-JSON <details> fallback) and NEVER
 * throws: payloads are untyped wire JSON and a thrown render blanks the
 * panel.
 */
export function summarizeAutoResearchEvent(
  ev: { kind: string; payload?: Record<string, unknown> | null },
): string | null {
  try {
    const p = (ev.payload ?? {}) as Record<string, unknown>;
    switch (ev.kind) {
      case "proposal_rejected": {
        const reason = typeof p.reason === "string" ? p.reason : null;
        return reason ? `rejected: ${reason}` : null;
      }
      case "run_enqueued":
        return summarizeOverlay(p);
      case "action_space": {
        const levers = Array.isArray(p.levers)
          ? (p.levers as unknown[]).filter(
              (x): x is string => typeof x === "string",
            )
          : [];
        const base = levers.length > 0 ? `levers: ${levers.join(", ")}` : null;
        if (p.all_click_dead === true) {
          return `${base ?? "levers: fv_scale"} · clicks dead on every corner`;
        }
        return base;
      }
      case "claude_requested_stop": {
        const r =
          typeof p.rationale === "string" && p.rationale.trim() !== ""
            ? p.rationale.trim()
            : null;
        return r ? `stop requested: ${r}` : "stop requested";
      }
      case "engineer_steering": {
        // INC2-C: an engineer's standing-guidance message, posted
        // mid-study and folded into every subsequent proposal turn.
        // Surface it inline so the sent text reads back in the log.
        const m =
          typeof p.message === "string" && p.message.trim() !== ""
            ? p.message.trim()
            : null;
        return m ? `steering: ${m}` : null;
      }
      case "claude_call_error": {
        const cls =
          typeof p.error_class === "string" ? p.error_class : "error";
        const msg = typeof p.message === "string" ? p.message : null;
        return msg ? `${cls}: ${msg}` : cls;
      }
      case "claude_call_complete": {
        const result = typeof p.result === "string" ? p.result : null;
        if (result) return result;
        const usage =
          p.usage && typeof p.usage === "object"
            ? (p.usage as Record<string, unknown>)
            : null;
        if (usage) {
          const i = asFiniteNumber(usage.input_tokens);
          const o = asFiniteNumber(usage.output_tokens);
          if (i !== null || o !== null) {
            return `tokens in/out: ${i ?? "?"}/${o ?? "?"}`;
          }
        }
        return null;
      }
      case "claude_call_start": {
        const attempt = asFiniteNumber(p.attempt);
        return attempt !== null ? `attempt ${attempt}` : null;
      }
      case "driver_tick_start": {
        const idx = asFiniteNumber(p.proposal_index);
        return idx !== null ? `tick, proposal #${idx}` : null;
      }
      case "driver_tick_skipped": {
        const reason = typeof p.reason === "string" ? p.reason : null;
        return reason ? `skipped: ${reason}` : null;
      }
      case "run_complete": {
        const runId = asFiniteNumber(p.run_id);
        return runId !== null ? `run ${runId} complete` : null;
      }
      case "cache_miss_warning":
        return "prompt cache miss (cost warning)";
      case "cache_miss_failure":
        return "prompt cache miss (failed closed)";
      case "session_terminal": {
        const bits: string[] = [];
        if (typeof p.state === "string") bits.push(p.state);
        const score = asFiniteNumber(p.best_score);
        if (score !== null) bits.push(`best ${_round4(score)}`);
        const reason =
          typeof p.reason === "string"
            ? p.reason
            : typeof p.error === "string"
              ? p.error
              : null;
        if (reason) bits.push(reason);
        return bits.length > 0 ? bits.join(" · ") : null;
      }
      case "summary_written": {
        const h = typeof p.headline === "string" ? p.headline : null;
        return h ? `summary: ${h}` : null;
      }
      case "summary_failed": {
        const fb = typeof p.fallback === "string" ? p.fallback : null;
        return fb ? `summary failed, fallback: ${fb}` : "summary failed";
      }
      case "protocol_enabled":
        return "protocol study enabled (design, screen, exploit, verify)";
      case "protocol_design": {
        // First hypothesis + the screen budget fraction: the plan at a glance.
        const hyps = Array.isArray(p.hypotheses)
          ? (p.hypotheses as unknown[]).filter(
              (x): x is string => typeof x === "string",
            )
          : [];
        const frac = asFiniteNumber(p.screen_fraction);
        const bits: string[] = [];
        if (hyps.length > 0) bits.push(`H1: ${hyps[0]}`);
        if (frac !== null) bits.push(`screen ${Math.round(frac * 100)}%`);
        return bits.length > 0 ? `design: ${bits.join(" · ")}` : "design";
      }
      case "protocol_phase": {
        const phase = typeof p.phase === "string" ? p.phase : null;
        return phase ? `phase: ${phase}` : null;
      }
      case "protocol_screen_member": {
        const idx = asFiniteNumber(p.index);
        const action = typeof p.action === "string" ? p.action : null;
        if (idx === null || !action) return null;
        const reason = typeof p.reason === "string" ? ` (${p.reason})` : "";
        return `screen member ${idx}: ${action}${reason}`;
      }
      case "reachability": {
        const lo = asFiniteNumber(p.objective_lo);
        const hi = asFiniteNumber(p.objective_hi);
        if (lo === null || hi === null) {
          return "reachability: screen recorded no scored member";
        }
        return `reachability: objective in [${_round4(lo)}, ${_round4(hi)}]`;
      }
      case "protocol_warning": {
        const m = typeof p.message === "string" ? p.message : null;
        return m ? `protocol note: ${m}` : null;
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}


// ---------- Predicted-vs-actual reconciliation ----------

export type PredictionReconRow = {
  kpi: string;
  predicted: number | null;
  actual: number | null;
  signedError: number | null;
  agreement: "agree" | "disagree" | "na";
};

/**
 * Reconcile Claude's logged predicted KPI deltas against the deltas the
 * member run actually realised. Predicted deltas are logged only (never
 * scored); actuals are the member run's kpi_scores minus the session
 * baseline's, joined by KPI id (predicted deltas are stated "vs the
 * session baseline"). When a member has no completed kpi_scores
 * (rejected / in-flight / failed) or the baseline KPIs are absent,
 * `actual` and `signedError` are null.
 *
 * `agreement` is 'agree' when predicted and actual share a sign (Claude
 * called the direction), 'disagree' when they oppose, and 'na' when
 * either side is unavailable or the prediction was a flat zero (no
 * direction to verify).
 */
export function reconcilePredictedDeltas(
  predicted: Record<string, unknown> | null | undefined,
  memberKpis: Record<string, unknown> | null | undefined,
  baselineKpis: Record<string, unknown> | null | undefined,
): PredictionReconRow[] {
  if (!predicted || typeof predicted !== "object") return [];
  const rows: PredictionReconRow[] = [];
  for (const [kpi, predRaw] of Object.entries(predicted)) {
    const pred = asFiniteNumber(predRaw);
    let actual: number | null = null;
    if (memberKpis && baselineKpis) {
      const mv = asFiniteNumber(memberKpis[kpi]);
      const bv = asFiniteNumber(baselineKpis[kpi]);
      if (mv !== null && bv !== null) actual = mv - bv;
    }
    const signedError =
      pred !== null && actual !== null ? actual - pred : null;
    let agreement: PredictionReconRow["agreement"] = "na";
    if (pred !== null && actual !== null && pred !== 0) {
      agreement =
        (pred > 0 && actual > 0) || (pred < 0 && actual < 0)
          ? "agree"
          : "disagree";
    }
    rows.push({ kpi, predicted: pred, actual, signedError, agreement });
  }
  return rows;
}


// ---------- T3 Pareto front view ----------

export type ParetoFrontMember = {
  run_id: number;
  run_number: number | null;
  scores: Record<string, number | null>;
  on_front: boolean;
};

export type ParetoFrontResponse = {
  session_id: number;
  is_pareto: boolean;
  objectives: { id: string; display_name: string; direction: "min" | "max" }[];
  members: ParetoFrontMember[];
};

// Recomputed live from completed members, so it can be polled during a run.
// Returns is_pareto=false for single-objective sessions (caller no-ops).
export async function fetchParetoFront(
  sessionId: number,
): Promise<ParetoFrontResponse> {
  const r = await fetch(
    `/api/auto_research/sessions/${sessionId}/pareto_front`,
  );
  if (!r.ok) {
    throw new Error(
      `GET pareto_front for session ${sessionId} failed: ${r.status}`,
    );
  }
  return (await r.json()) as ParetoFrontResponse;
}

export async function startAutoResearch(
  testId: number,
  body: StartAutoResearchBody = {},
): Promise<AutoResearchSession> {
  const r = await fetch(`/api/tests/${testId}/auto_research/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(
      `POST /api/tests/${testId}/auto_research/start failed: ${r.status}: ${text}`,
    );
  }
  return AutoResearchSessionSchema.parse(await r.json());
}

export async function getAutoResearchSession(
  sessionId: number,
): Promise<AutoResearchSession> {
  const r = await fetch(`/api/auto_research/sessions/${sessionId}`);
  if (!r.ok) {
    throw new Error(
      `GET /api/auto_research/sessions/${sessionId} failed: ${r.status}`,
    );
  }
  return AutoResearchSessionSchema.parse(await r.json());
}

export async function stopAutoResearch(
  sessionId: number,
): Promise<AutoResearchSession> {
  const r = await fetch(
    `/api/auto_research/sessions/${sessionId}/stop`,
    { method: "POST" },
  );
  if (!r.ok) {
    const text = await r.text();
    throw new Error(
      `POST /api/auto_research/sessions/${sessionId}/stop failed: ${r.status}: ${text}`,
    );
  }
  return AutoResearchSessionSchema.parse(await r.json());
}


// ---------- Mid-study engineer steering (INC2-C) ----------

const AutoResearchSteeringResponseSchema = z.object({
  ok: z.boolean(),
  // id of the appended engineer_steering event. The backend derives it
  // with (ev or {}).get("id"), so tolerate a null.
  event_id: z.number().int().nullable(),
  session_id: z.number().int(),
});

export type AutoResearchSteeringResponse = z.infer<
  typeof AutoResearchSteeringResponseSchema
>;

/**
 * Post a standing engineer-steering message to a RUNNING Auto Research
 * session. The backend appends it to the session event log
 * (kind="engineer_steering", payload={message}); the driver renders
 * every steering message into each subsequent proposal turn as standing
 * guidance. Text-only advisory, never a schema or action-space change.
 *
 * 200 → {ok, event_id, session_id}. Non-2xx throws with the status in
 * the message: 404 unknown session, 409 the session is not running (a
 * terminal session has no future turns to steer), 422 empty or
 * over-2000-char message. The caller surfaces a one-line inline notice.
 */
export async function postAutoResearchSteering(
  sessionId: number,
  message: string,
  signal?: AbortSignal,
): Promise<AutoResearchSteeringResponse> {
  const r = await fetch(
    `/api/auto_research/sessions/${sessionId}/steering`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message }),
      signal,
    },
  );
  if (!r.ok) {
    const text = await r.text();
    throw new Error(
      `POST /api/auto_research/sessions/${sessionId}/steering failed: `
      + `${r.status}: ${text}`,
    );
  }
  return AutoResearchSteeringResponseSchema.parse(await r.json());
}

const _ListSchema = z.array(AutoResearchSessionListItemSchema);

export async function listAutoResearchSessions(
  testId: number,
): Promise<AutoResearchSessionListItem[]> {
  const r = await fetch(`/api/tests/${testId}/auto_research/sessions`);
  if (!r.ok) {
    throw new Error(
      `GET /api/tests/${testId}/auto_research/sessions failed: ${r.status}`,
    );
  }
  return _ListSchema.parse(await r.json());
}
